import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { inputStyle, btnPrimary } from './CommissionOverview';
import { FormRow } from './CommissionBeneficiaries';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const DEFAULTS = {
  applicability_default: true,
  entry_prefix: 'COM', entry_next_number: 1, entry_number_length: 6,
  profile_prefix: 'CPR', profile_next_number: 1,
  rule_prefix: 'CRL', rule_next_number: 1,
  agreement_prefix: 'CAG', agreement_next_number: 1,
  beneficiary_prefix: 'CBN', beneficiary_next_number: 1,
  adjustment_prefix: 'CAJ', adjustment_next_number: 1,
  settlement_prefix: 'SET', settlement_next_number: 1,
  batch_prefix: 'BAT', batch_next_number: 1,
  number_separator: '-',
  default_calculation_basis: 'after_discount',
  default_tax_treatment: 'after_tax',
  default_earning_trigger: 'customer_payment',
  partial_payment_proportional: true,
  default_stack_replace: 'replace',
  overlap_conflict_detection: true,
  default_tier_method: 'progressive',
  min_max_enabled: true,
  default_cap_type: 'no_cap',
  cap_interaction: 'per_rule',
  recurring_generation_timing: 'on_trigger',
  referral_ownership_default: 'assigned',
  referral_validity_days: '',
  default_holding_period_days: 0,
  default_payment_allocation: 'oldest_due_first',
  approval_threshold_amount: '',
  settlement_approval_required: true,
  negative_balance_default_handling: 'manager_decides',
  refund_review_required: true,
  recalculation_default_mode: 'original_snapshot',
  paid_entry_recalculation_protection: true,
  beneficiary_requires_erp_user: true,
  inactive_beneficiary_effect: 'stop_future_only',
  cross_company_commission_enabled: true,
  auto_expense_handoff: true,
  expense_category: '',
  own_commission_dashboard_enabled: true,
  confidentiality_restricted: true,
  notify_entry_calculated: true,
  notify_entry_due: true,
  notify_settlement_approved: true,
  notify_settlement_paid: true,
  notify_adjustment_made: true,
  notify_refund_review: true,
};

export default function CommissionSettings() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/settings`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      const merged = { ...DEFAULTS };
      Object.keys(DEFAULTS).forEach(k => { merged[k] = json[k] === null || json[k] === undefined ? DEFAULTS[k] : json[k]; });
      setForm(merged);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const u = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const submit = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/settings`, {
        method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to save');
      setMsg('Settings saved successfully.');
    } catch (e) { setMsg(e.message); }
    setSaving(false);
  };

  if (initialLoading) return <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>;

  const Toggle = ({ field, label }) => (
    <FormRow label={label}><input type="checkbox" checked={!!form[field]} onChange={e => u(field, e.target.checked)} /></FormRow>
  );

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', maxWidth: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>⚙️ Commission Settings</h2>
        <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
      </div>
      {msg && <div style={{ marginBottom: 16, fontSize: 13, color: msg.includes('success') ? '#10b981' : '#ef4444' }}>{msg}</div>}

      <Section title="General">
        <Toggle field="applicability_default" label="Applicable by Default" />
      </Section>

      <Section title="Numbering">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[['entry_prefix', 'Entry Prefix'], ['profile_prefix', 'Profile Prefix'], ['rule_prefix', 'Rule Prefix'],
            ['agreement_prefix', 'Agreement Prefix'], ['beneficiary_prefix', 'Beneficiary Prefix'], ['adjustment_prefix', 'Adjustment Prefix'],
            ['settlement_prefix', 'Settlement Prefix'], ['batch_prefix', 'Batch Prefix'], ['number_separator', 'Number Separator']].map(([f, l]) => (
            <FormRow key={f} label={l}><input value={form[f]} onChange={e => u(f, e.target.value)} style={inputStyle} /></FormRow>
          ))}
          <FormRow label="Entry Number Length"><input type="number" value={form.entry_number_length} onChange={e => u('entry_number_length', e.target.value)} style={inputStyle} /></FormRow>
        </div>
      </Section>

      <Section title="Calculation Defaults">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <FormRow label="Default Calculation Basis">
            <select value={form.default_calculation_basis} onChange={e => u('default_calculation_basis', e.target.value)} style={inputStyle}>
              {['gross_sale', 'net_sale', 'after_discount', 'amount_collected', 'profit', 'margin', 'product_value', 'service_value', 'custom'].map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          <FormRow label="Default Tax Treatment">
            <select value={form.default_tax_treatment} onChange={e => u('default_tax_treatment', e.target.value)} style={inputStyle}>
              <option value="before_tax">Before Tax</option><option value="after_tax">After Tax</option>
            </select>
          </FormRow>
        </div>
      </Section>

      <Section title="Trigger">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <FormRow label="Default Earning Trigger">
            <select value={form.default_earning_trigger} onChange={e => u('default_earning_trigger', e.target.value)} style={inputStyle}>
              {['deal_won', 'contract_confirmed', 'invoice_issued', 'customer_payment', 'full_settlement', 'custom'].map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          <Toggle field="partial_payment_proportional" label="Partial Payment Proportional" />
        </div>
      </Section>

      <Section title="Priority & Stacking">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <FormRow label="Default Stack/Replace">
            <select value={form.default_stack_replace} onChange={e => u('default_stack_replace', e.target.value)} style={inputStyle}>
              <option value="stack">Stack</option><option value="replace">Replace</option>
            </select>
          </FormRow>
          <Toggle field="overlap_conflict_detection" label="Overlap Conflict Detection" />
        </div>
      </Section>

      <Section title="Tiering">
        <FormRow label="Default Tier Method">
          <select value={form.default_tier_method} onChange={e => u('default_tier_method', e.target.value)} style={inputStyle}>
            <option value="full_amount">Full Amount</option><option value="progressive">Progressive</option>
          </select>
        </FormRow>
      </Section>

      <Section title="Min / Max / Cap">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Toggle field="min_max_enabled" label="Min/Max Enabled" />
          <FormRow label="Default Cap Type">
            <select value={form.default_cap_type} onChange={e => u('default_cap_type', e.target.value)} style={inputStyle}>
              {['no_cap', 'per_transaction', 'per_month', 'per_quarter', 'per_year', 'per_agreement', 'per_beneficiary'].map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          <FormRow label="Cap Interaction">
            <select value={form.cap_interaction} onChange={e => u('cap_interaction', e.target.value)} style={inputStyle}>
              <option value="per_rule">Per Rule</option><option value="combined">Combined</option>
            </select>
          </FormRow>
        </div>
      </Section>

      <Section title="Recurring">
        <FormRow label="Recurring Generation Timing">
          <select value={form.recurring_generation_timing} onChange={e => u('recurring_generation_timing', e.target.value)} style={inputStyle}>
            <option value="on_trigger">On Trigger</option><option value="scheduled">Scheduled</option>
          </select>
        </FormRow>
      </Section>

      <Section title="Referral">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <FormRow label="Referral Ownership Default">
            <select value={form.referral_ownership_default} onChange={e => u('referral_ownership_default', e.target.value)} style={inputStyle}>
              <option value="first_touch">First Touch</option><option value="last_touch">Last Touch</option><option value="assigned">Assigned</option>
            </select>
          </FormRow>
          <FormRow label="Referral Validity (days)"><input type="number" value={form.referral_validity_days} onChange={e => u('referral_validity_days', e.target.value)} style={inputStyle} /></FormRow>
        </div>
      </Section>

      <Section title="Holding">
        <FormRow label="Default Holding Period (days)"><input type="number" value={form.default_holding_period_days} onChange={e => u('default_holding_period_days', e.target.value)} style={inputStyle} /></FormRow>
      </Section>

      <Section title="Settlement">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <FormRow label="Default Payment Allocation">
            <select value={form.default_payment_allocation} onChange={e => u('default_payment_allocation', e.target.value)} style={inputStyle}>
              <option value="oldest_due_first">Oldest Due First</option><option value="manual">Manual</option>
            </select>
          </FormRow>
          <FormRow label="Approval Threshold Amount"><input type="number" value={form.approval_threshold_amount} onChange={e => u('approval_threshold_amount', e.target.value)} style={inputStyle} /></FormRow>
          <Toggle field="settlement_approval_required" label="Settlement Approval Required" />
        </div>
      </Section>

      <Section title="Negative Balance">
        <FormRow label="Default Handling">
          <select value={form.negative_balance_default_handling} onChange={e => u('negative_balance_default_handling', e.target.value)} style={inputStyle}>
            {['carry_forward', 'deduct_future', 'request_repayment', 'write_off', 'manager_decides'].map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
          </select>
        </FormRow>
      </Section>

      <Section title="Refund / Cancellation">
        <Toggle field="refund_review_required" label="Refund Review Required" />
      </Section>

      <Section title="Recalculation">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <FormRow label="Default Mode">
            <select value={form.recalculation_default_mode} onChange={e => u('recalculation_default_mode', e.target.value)} style={inputStyle}>
              <option value="original_snapshot">Original Snapshot</option><option value="current_rules">Current Rules</option><option value="manual_override">Manual Override</option>
            </select>
          </FormRow>
          <Toggle field="paid_entry_recalculation_protection" label="Paid Entry Recalculation Protection" />
        </div>
      </Section>

      <Section title="Beneficiary">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <Toggle field="beneficiary_requires_erp_user" label="Beneficiary Requires ERP User" />
          <FormRow label="Inactive Beneficiary Effect">
            <select value={form.inactive_beneficiary_effect} onChange={e => u('inactive_beneficiary_effect', e.target.value)} style={inputStyle}>
              <option value="stop_future_only">Stop Future Only</option><option value="suspend_all">Suspend All</option>
            </select>
          </FormRow>
        </div>
      </Section>

      <Section title="Cross-Company">
        <Toggle field="cross_company_commission_enabled" label="Cross-Company Commission Enabled" />
      </Section>

      <Section title="Financial Integration">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <Toggle field="auto_expense_handoff" label="Auto Expense Handoff" />
          <FormRow label="Expense Category"><input value={form.expense_category} onChange={e => u('expense_category', e.target.value)} style={inputStyle} /></FormRow>
        </div>
      </Section>

      <Section title="Self-Service / Confidentiality">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <Toggle field="own_commission_dashboard_enabled" label="Own Commission Dashboard Enabled" />
          <Toggle field="confidentiality_restricted" label="Confidentiality Restricted" />
        </div>
      </Section>

      <Section title="Notifications">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Toggle field="notify_entry_calculated" label="Entry Calculated" />
          <Toggle field="notify_entry_due" label="Entry Due" />
          <Toggle field="notify_settlement_approved" label="Settlement Approved" />
          <Toggle field="notify_settlement_paid" label="Settlement Paid" />
          <Toggle field="notify_adjustment_made" label="Adjustment Made" />
          <Toggle field="notify_refund_review" label="Refund Review" />
        </div>
      </Section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, marginBottom: 40 }}>
        <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18, marginBottom: 16 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginTop: 0, marginBottom: 14 }}>{title}</h3>
      {children}
    </div>
  );
}
